/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

import {
  useCreateTrafficFilterRulesetAssociationMutation,
  useListTrafficFilterRulesetsQuery,
} from '@modules/cloud-lib/networkSecurity/hooks'
import type { RulesetType } from '@modules/network-security-lib/types'
import type { DeploymentGetResponse } from '@modules/cloud-api/v1/types'
import { addToast } from '@modules/cui/Toasts'

import { getRegionId } from '@/lib/stackDeployments/selectors/fundamentals' // eslint-disable-line import/no-restricted-paths

import IpFilterForm from './IpFilterForm'
import PrivateConnectionForm from './PrivateConnectionForm'

type Props = {
  rulesetType: RulesetType
  onClose: () => void
  deployment: DeploymentGetResponse
}

const TrafficFilterForm: React.FunctionComponent<Props> = ({
  rulesetType,
  onClose,
  deployment,
}) => {
  const { formatMessage } = useIntl()

  const { data } = useListTrafficFilterRulesetsQuery()

  const { mutate: createTrafficFilterRulesetAssociation, isLoading: isMutating } =
    useCreateTrafficFilterRulesetAssociationMutation()

  const regionId = getRegionId({ deployment })

  // show only rulesets that match the deployment's region and aren't already associated
  const filteredRulesets = useMemo(
    () =>
      (data?.rulesets || []).filter(
        (ruleset) =>
          ruleset.region === regionId &&
          !ruleset.associations?.some(
            (association) =>
              association.entity_type === 'deployment' && association.id === deployment.id,
          ),
      ),
    [data?.rulesets, regionId, deployment.id],
  )

  const save = (rulesetId: string) => {
    createTrafficFilterRulesetAssociation(
      {
        rulesetId,
        association: {
          entity_type: 'deployment',
          id: deployment.id,
        },
      },
      {
        onSettled: () => {
          onClose()
        },
        onError: () => {
          addToast({
            title: formatMessage({
              id: 'trafficFilter.association.error.title',
              defaultMessage: 'Error',
            }),
            color: 'danger',
            iconType: 'error',
            text: formatMessage({
              id: 'trafficFilter.association.error.description',
              defaultMessage: 'Failed to apply traffic filter',
            }),
          })
        },
      },
    )
  }

  return rulesetType === 'ipFilter' ? (
    <IpFilterForm
      rulesets={filteredRulesets}
      onClose={onClose}
      onSave={save}
      isSaving={isMutating}
    />
  ) : (
    <PrivateConnectionForm
      rulesets={filteredRulesets}
      onClose={onClose}
      onSave={save}
      isSaving={isMutating}
    />
  )
}

export default TrafficFilterForm
